import { TYPE } from "vue-toastification";
import { apiCall, apiCallParams, logResponse } from "@/core/api";
import { showAlert } from "@/core/popup";
import REQUEST_TYPE from "@/core/api/request-types";

export interface StepConnection {
  stepId: string;
  stepName: string;
  specId: string;
  requiresCreate: boolean;
  connectionId: string;
  connection: any;
  addNew: boolean;
  nameExists: boolean;
  uniqueConnection: boolean;
  selectedName: string;
  // true for steps using a connection-optional connector (no Connection required).
  connectionOptional?: boolean;
  // true for virtual steps (LOOP / LOOP_END) that have no connector and need no connection mapping.
  isVirtualStep?: boolean;
}

export async function getConnectionList(): Promise<any> {
  const resource = "/connections";
  const response = await apiCall("GET", resource);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function getOrganizationConnectionList(
  organizationId: string
): Promise<any> {
  const resource = `/connections/organization/${organizationId}`;
  const response = await apiCall("GET", resource);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function getConnection(connectionId: string): Promise<any> {
  const endpoint = `/connections/find/${connectionId}`;
  const response = await apiCall("GET", endpoint);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function saveConnection(
  connection: any,
  selectedOrgId: string
): Promise<any> {
  const endpoint = "/connections/add";
  const response = await apiCall(
    "POST",
    endpoint,
    connection,
    REQUEST_TYPE.STANDART,
    selectedOrgId
  );
  if (response.code === 200) {
    if (response.result?.code === "1011") {
      showAlert(TYPE.WARNING, "Connection with the same name exists.");
      return undefined;
    }
    return response.result[0];
  }
  if (response.code === 406) {
    showAlert(TYPE.WARNING, response.message);
    return undefined;
  }
  logResponse(response);
  return undefined;
}

export async function editConnection(connection: any): Promise<any> {
  const endpoint = "/connections";
  const response = await apiCall("PUT", endpoint, connection);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function deleteConnection(connectionId: string): Promise<any> {
  const resource = `/connections/${connectionId}`;
  const response = await apiCall("DELETE", resource);
  if (response.code === 200 || response.code === 409) {
    return response;
  }
  logResponse(response);
  return 500;
}

export async function getConnectorList(): Promise<any> {
  const resource = "/connectors";
  const response = await apiCall("GET", resource);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function getOrganizationsConnectorList(): Promise<any> {
  const resource = "/connectors/organization";
  const response = await apiCall("GET", resource);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function getConnectorSpecs(): Promise<any> {
  const resource = "/registry";
  const response = await apiCall("GET", resource);
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function getConnectorSpecById(specId: string): Promise<any> {
  const resource = `/registry/${specId}`;
  const response = await apiCall("GET", resource);
  if (response.code === 200 && response.result != null) {
    // The /registry endpoint returns the spec as a String (ResponseEntity<String>),
    // which can arrive either already parsed (object) or as a raw JSON string
    // depending on the response content-type. Normalize to an object.
    let spec = response.result;
    if (typeof spec === "string") {
      try {
        spec = JSON.parse(spec);
      } catch (error) {
        logResponse(response);
        return undefined;
      }
    }
    return {
      spec,
      connectionSetting: spec?.connectionSettingsSchemas,
    };
  }
  logResponse(response);
  return undefined;
}

/**
 * Fetches the dynamic-connector spec-generation status for a connection's latest revision.
 * Returns the status DTO ({ status, lastError, lastAttemptAt, revision, hasSpec }), or undefined
 * when the connection has no dynamic spec (non-dynamic connector, or generation never attempted).
 */
export async function getDynamicSpecStatus(connectionId: string): Promise<any> {
  const endpoint = `/connectorui/dynamic/${connectionId}/status`;
  const response = await apiCall(
    "GET",
    endpoint,
    undefined,
    REQUEST_TYPE.CONNECTOR
  );
  if (response.code === 200 && response.result != null) {
    return response.result;
  }
  return undefined;
}

export async function getConnectionListing(params: any): Promise<any> {
  const endpoint = "/connections/listing";
  const response = await apiCallParams("GET", endpoint, params);
  if (response.code === 200) {
    return response.page;
  }
  logResponse(response);
  return undefined;
}
export async function checkIfConnectionUsedInFlows(
  connectionId: string
): Promise<any> {
  const endpoint = `/connections/flows/${connectionId}`;
  try {
    const response = await apiCallParams("GET", endpoint);
    if (response.code === 200) {
      return response;
    }
    logResponse(response);
    return { error: true, message: "Failed to check connection usage." };
  } catch (error) {
    console.error("An error occurred:", error);
    return { error: true, message: "An error occurred." };
  }
}

export async function inLineEditConnection(
  connectionId: string,
  connection: any
): Promise<any> {
  const endpoint = `/connections/edit/${connectionId}`;
  const response = await apiCall("POST", endpoint, connection);
  if (response.code === 200) {
    if (response.result?.code === "1011") {
      showAlert(TYPE.WARNING, "Connection with the same name exists.");
      return undefined;
    }
    return true;
  }
  logResponse(response);
  return undefined;
}

export function convertToDefinitions(data: any): any {
  if (data) {
    const definitions = {};
    Object.entries(data).forEach(([key, val]: any[]) => {
      const value = val === "*****" ? null : val;
      definitions[key] = {
        name: key,
        value,
      };
    });
    return definitions;
  }
  return undefined;
}

export function convertObject(data: any): any {
  const object = {};
  if (data) {
    Object.entries(data).forEach(([key, value]: any[]) => {
      object[key] = value.value;
    });
  }
  return object;
}

export async function getOAuthUrl(
  connectionId: string,
  selectedOrgId?: string
): Promise<any> {
  const endpoint = "/oauth2/generate-authorization-url";
  const response = await apiCallParams(
    "GET",
    endpoint,
    {
      connectionId,
    },
    REQUEST_TYPE.STANDART,
    selectedOrgId
  );

  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function verifyOauth(connectionId: string): Promise<any> {
  const endpoint = "/oauth2/verifyConnectivity";
  const response = await apiCallParams("GET", endpoint, {
    connectionId,
  });
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function disconnectOAuth(connectionId: string): Promise<any> {
  const endpoint = "/oauth2/disconnectOAuthConnectivity";
  const response = await apiCallParams("GET", endpoint, {
    connectionId,
  });
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}

export async function getConnectorAuthSchemas(
  connectorSpecId: string
): Promise<any> {
  const endpoint = "/connectors/spec";
  const response = await apiCallParams("GET", endpoint, {
    connectorSpecId,
  });
  if (response.code === 200) {
    return response.result;
  }
  logResponse(response);
  return undefined;
}
